import React, { useState, useEffect, useRef } from 'react';
import ChangePassword from './ChangePassword';
import Footer from './Footer';
import API_URL from '../config/api';
import './UserDashboard.css';

export default function UserDashboard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  const token = localStorage.getItem('token');

  useEffect(() => { fetchProfile(); }, []);

  // Real-time clock — ticks every second
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) setProfile(data.user);
      else setError(data.message);
    } catch (err) {
      setError('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const handleCopyMachine = () => {
    if (profile?.bound_machine) {
      navigator.clipboard.writeText(profile.bound_machine);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    });
  };

  const getGreeting = () => {
    const hour = now.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  // Returns { text, color, expired, days, hours, mins, secs, diff }
  const getCountdown = (until) => {
    if (!until) return { text: 'N/A', color: '#999', expired: true, days: 0, hours: 0, mins: 0, secs: 0, diff: 0 };
    const diff = new Date(until) - now;
    if (diff <= 0) return { text: '⛔ Expired', color: '#e53935', expired: true, days: 0, hours: 0, mins: 0, secs: 0, diff: 0 };

    const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins  = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const secs  = Math.floor((diff % (1000 * 60)) / 1000);

    let text;
    if (days > 0)       text = `${days}d ${hours}h ${mins}m ${secs}s`;
    else if (hours > 0) text = `${hours}h ${mins}m ${secs}s`;
    else if (mins > 0)  text = `${mins}m ${secs}s`;
    else                text = `${secs}s`;

    let color;
    if (diff < 3600000)        color = '#e53935'; // < 1 hour — red
    else if (diff < 86400000)  color = '#ff9800'; // < 1 day — orange
    else if (diff < 259200000) color = '#f39c12'; // < 3 days — yellow
    else                       color = '#2e7d32'; // healthy — green

    return { text, color, expired: false, days, hours, mins, secs, diff };
  };

  const getProgressPercentage = () => {
    if (!profile?.totalDays || !profile?.daysLeft) return 0;
    const used = profile.totalDays - profile.daysLeft;
    return Math.round((used / profile.totalDays) * 100);
  };

  const getProgressColor = (diff) => {
    if (!diff || diff <= 0) return 'linear-gradient(90deg, #e53935, #ef5350)';
    if (diff < 86400000)    return 'linear-gradient(90deg, #e53935, #ef5350)';
    if (diff < 604800000)   return 'linear-gradient(90deg, #fb8c00, #ffa726)';
    return 'linear-gradient(90deg, #43a047, #66bb6a)';
  };

  if (loading) return <div className="ud-loading">Loading...</div>;
  if (error && !profile) return <div className="ud-error-page">{error}</div>;

  const licenseCountdown = getCountdown(profile?.available_until);
  const bindCountdown    = getCountdown(profile?.machine_bound_until);
  const licenseDiff      = profile?.available_until ? new Date(profile.available_until) - now : 0;
  const isBound          = profile?.bound_machine && new Date(profile?.machine_bound_until) > now;

  return (
    <div className="ud-root">

      {/* HEADER */}
      <header className="ud-header">
        <div className="ud-header-brand">
          <span className="ud-brand-icon">🐯</span>
          <span className="ud-brand-name">Tiger Guard</span>
        </div>

        <div className="ud-header-desktop-actions">
          <span className="ud-username-badge">{profile?.username}</span>
          <span className="ud-role-badge">User</span>
          <span className={`ud-license-pill ${profile?.active ? 'active' : 'inactive'}`}>
            {profile?.active ? '✓ Active' : '✗ Inactive'}
          </span>
          <button className="ud-btn-icon" title="Change Password" onClick={() => setShowChangePassword(true)}>🔐</button>
          <button className="ud-btn-logout" onClick={handleLogout}>Logout</button>
        </div>

        <button
          className={`ud-hamburger ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(v => !v)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </header>

      {/* MOBILE DRAWER */}
      <div className={`ud-mobile-drawer ${mobileMenuOpen ? 'visible' : ''}`} ref={menuRef}>
        <div className="ud-drawer-profile">
          <div className="ud-drawer-avatar">🐯</div>
          <div>
            <div className="ud-drawer-username">{profile?.username}</div>
            <div className="ud-role-badge" style={{ marginTop: 4 }}>User</div>
          </div>
          <span className={`ud-license-pill ${profile?.active ? 'active' : 'inactive'}`} style={{ marginLeft: 'auto' }}>
            {profile?.active ? '✓ Active' : '✗ Inactive'}
          </span>
        </div>

        <div className="ud-drawer-time">
          <div className="ud-drawer-timer" style={{ background: `linear-gradient(135deg, #3a7bd5, #00d2ff)` }}>
            <div className="ud-drawer-timer-value" style={{ color: licenseCountdown.color, fontFamily: 'monospace', fontSize: '15px' }}>
              {licenseCountdown.text}
            </div>
            <div className="ud-drawer-timer-label">License Countdown</div>
          </div>

          {isBound && (
            <div className="ud-drawer-timer" style={{ background: `linear-gradient(135deg, #764ba2, #667eea)`, marginTop: 10 }}>
              <div className="ud-drawer-timer-value" style={{ color: bindCountdown.color, fontFamily: 'monospace', fontSize: '15px' }}>
                {bindCountdown.text}
              </div>
              <div className="ud-drawer-timer-label">Until Bind Resets</div>
            </div>
          )}

          <div className="ud-drawer-expiry">
            <div className="ud-drawer-expiry-row">
              <span>📅 Created</span>
              <span>{formatDateTime(profile?.created_at)}</span>
            </div>
            <div className="ud-drawer-expiry-row">
              <span>🗓️ Expires</span>
              <span>{formatDateTime(profile?.available_until)}</span>
            </div>
            <div className="ud-drawer-expiry-row">
              <span>📦 Package</span>
              <span>{profile?.package_type || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="ud-drawer-actions">
          <button className="ud-drawer-btn secondary" onClick={() => { setShowChangePassword(true); setMobileMenuOpen(false); }}>
            🔐 Change Password
          </button>
          <button className="ud-drawer-btn danger" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </div>

      {mobileMenuOpen && <div className="ud-backdrop" onClick={() => setMobileMenuOpen(false)} />}

      {/* MAIN */}
      <main className="ud-main">

        {error && <div className="ud-alert error">{error}</div>}
        {success && <div className="ud-alert success">{success}</div>}

        {/* WELCOME BANNER */}
        <div className="ud-welcome">
          <div className="ud-welcome-avatar">
            {profile?.username?.slice(0, 2).toUpperCase()}
          </div>
          <div className="ud-welcome-text">
            <h2>
              {getGreeting()}, <span className="ud-welcome-name">{profile?.username}</span>! 👋
            </h2>
            <p>Here's your license information and current status</p>
            <div className="ud-welcome-clock">
              {now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <div className="ud-welcome-badges">
              <div className="ud-welcome-status">
                <span className={`ud-welcome-dot ${profile?.active ? 'active' : 'inactive'}`}></span>
                {profile?.active ? 'License Active' : 'License Inactive'}
              </div>
              <div className="ud-welcome-package">📦 {profile?.package_type || 'No Package'}</div>
            </div>
          </div>
        </div>

        {/* QUICK STATS ROW */}
        <div className="ud-stats-row">
          <div className="ud-stat-card">
            <span className="ud-stat-icon">📦</span>
            <span className="ud-stat-value">{profile?.package_type || 'N/A'}</span>
            <span className="ud-stat-label">Package</span>
          </div>
          <div className="ud-stat-card">
            <span className="ud-stat-icon">🖥️</span>
            <span className="ud-stat-value">{isBound ? 'Bound' : 'Free'}</span>
            <span className="ud-stat-label">Machine</span>
          </div>
          <div className="ud-stat-card">
            <span className="ud-stat-icon">{profile?.active ? '✅' : '❌'}</span>
            <span className="ud-stat-value">{profile?.active ? 'Active' : 'Inactive'}</span>
            <span className="ud-stat-label">Status</span>
          </div>
        </div>

        {/* LICENSE COUNTDOWN CARD */}
        <div className="ud-card ud-countdown-card">
          <div className="ud-card-header">
            <h3>⏱️ License Time Remaining</h3>
            <span className={`ud-status-badge ${profile?.active ? 'active' : 'inactive'}`}>
              {profile?.active ? '✓ Active' : '✗ Inactive'}
            </span>
          </div>

          {/* TIME BLOCKS */}
          {!licenseCountdown.expired ? (
            <div className="ud-countdown-blocks">
              {licenseCountdown.days > 0 && (
                <div className="ud-time-block" style={{ borderColor: licenseCountdown.color + '55' }}>
                  <span style={{ color: licenseCountdown.color }}>{licenseCountdown.days}</span>
                  <label>Days</label>
                </div>
              )}
              <div className="ud-time-block" style={{ borderColor: licenseCountdown.color + '55' }}>
                <span style={{ color: licenseCountdown.color }}>{String(licenseCountdown.hours).padStart(2, '0')}</span>
                <label>Hours</label>
              </div>
              <div className="ud-time-block" style={{ borderColor: licenseCountdown.color + '55' }}>
                <span style={{ color: licenseCountdown.color }}>{String(licenseCountdown.mins).padStart(2, '0')}</span>
                <label>Mins</label>
              </div>
              <div className="ud-time-block" style={{ borderColor: licenseCountdown.color + '55' }}>
                <span style={{ color: licenseCountdown.color }}>{String(licenseCountdown.secs).padStart(2, '0')}</span>
                <label>Secs</label>
              </div>
            </div>
          ) : (
            <div className="ud-big-countdown" style={{ color: licenseCountdown.color }}>
              {licenseCountdown.text}
            </div>
          )}

          <div className="ud-expiry-dates">
            <div className="ud-detail-item">
              <span className="ud-detail-label">📅 License Created</span>
              <span className="ud-detail-value">{formatDateTime(profile?.created_at)}</span>
            </div>
            <div className="ud-detail-item">
              <span className="ud-detail-label">🗓️ License Expires</span>
              <span className="ud-detail-value">{formatDateTime(profile?.available_until)}</span>
            </div>
            <div className="ud-detail-item">
              <span className="ud-detail-label">📦 Package</span>
              <span className="ud-detail-value">{profile?.package_type || 'N/A'}</span>
            </div>
          </div>

          <div className="ud-progress-wrap">
            <div className="ud-progress-bar">
              <div
                className="ud-progress-fill"
                style={{
                  width: `${getProgressPercentage()}%`,
                  background: getProgressColor(licenseDiff)
                }}
              />
            </div>
            <span className="ud-progress-text">{getProgressPercentage()}% used</span>
          </div>

          {licenseCountdown.expired && (
            <div className="ud-expiry-notice expired">❌ Your license has expired! Please contact your administrator.</div>
          )}
          {!licenseCountdown.expired && licenseDiff < 259200000 && licenseDiff > 0 && (
            <div className="ud-expiry-notice warning">⚠️ Your license is expiring soon! Contact your administrator to renew.</div>
          )}
        </div>

        {/* MACHINE BIND CARD */}
        <div className="ud-card ud-bind-card">
          <div className="ud-card-header">
            <h3>🔗 Machine Binding</h3>
            <span className={`ud-status-badge ${isBound ? 'active' : 'inactive'}`}>
              {isBound ? '🔒 Bound' : '🔓 Not Bound'}
            </span>
          </div>

          <div className="ud-info-grid">
            <div className="ud-info-row">
              <span className="ud-info-label">Bound Machine</span>
              <span className="ud-info-value" style={{ fontFamily: 'monospace', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {profile?.bound_machine || 'Not bound yet'}
                {profile?.bound_machine && (
                  <button className="ud-copy-btn" onClick={handleCopyMachine} title="Copy machine name">
                    {copied ? '✅' : '📋'}
                  </button>
                )}
              </span>
            </div>
            <div className="ud-info-row">
              <span className="ud-info-label">Bind Time</span>
              <span className="ud-info-value">
                {profile?.bind_time ? new Date(profile.bind_time).toLocaleString() : 'N/A'}
              </span>
            </div>
            <div className="ud-info-row">
              <span className="ud-info-label">Bind Resets At</span>
              <span className="ud-info-value">
                {profile?.machine_bound_until ? new Date(profile.machine_bound_until).toLocaleString() : 'N/A'}
              </span>
            </div>
          </div>

          {isBound ? (
            <div className="ud-bind-countdown-wrap">
              <div className="ud-bind-label">⏳ Time until machine bind resets:</div>
              <div className="ud-bind-countdown" style={{ color: bindCountdown.color }}>
                {bindCountdown.text}
              </div>
              <div className="ud-bind-note">After this time, you can log in from a different PC.</div>
            </div>
          ) : (
            <div className="ud-expiry-notice success" style={{ background: '#e8f5e9', color: '#2e7d32', borderColor: '#43a047' }}>
              ✅ Your machine bind has reset. You can now log in from any PC.
            </div>
          )}
        </div>
 {/* SOFTWARE DOWNLOAD CARD */}
<div className="ud-card ud-download-card">
  <div className="ud-card-header">
    <h3>⬇️ Download BL UNLOCK MTK FILES</h3>

    <span className="ud-status-badge active">
      Available
    </span>
  </div>

  <div className="ud-info-grid">
    <div className="ud-info-row">
      <span className="ud-info-label">Software</span>
      <span className="ud-info-value">
        BL UNLOCK FILES for MTK Devices
      </span>
    </div>

    <div className="ud-info-row">
      <span className="ud-info-label">Password</span>
      <span className="ud-info-value">
        tigerguard@4500
      </span>
    </div>

    <div className="ud-info-row">
      <span className="ud-info-label">Format</span>
      <span className="ud-info-value">
        ZIP Archive
      </span>
    </div>
  </div>

  <a
    href="https://www.mediafire.com/file/l8evpk1d1kgjljf/TIGER+GUARD+TOOL+MTK+BL+UNLOCK.rar/file"
    target="_blank"
    rel="noopener noreferrer"
    className="ud-download-btn"
  >
    ⬇️ Download BL UNLOCK MTK FILES
  </a>

  <p className="ud-download-note">
    Download and read the instructions carefully. If you encounter any issues, please contact your administrator for support.
  </p>
</div>
        {/* LICENSE DETAILS CARD */}
        <div className="ud-card ud-license-card">
          <div className="ud-card-header">
            <h3>📋 License Details</h3>
          </div>
          <div className="ud-info-grid">
            <div className="ud-info-row">
              <span className="ud-info-label">Username</span>
              <span className="ud-info-value">{profile?.username}</span>
            </div>
            <div className="ud-info-row">
              <span className="ud-info-label">Email</span>
              <span className="ud-info-value">{profile?.email || 'Not provided'}</span>
            </div>
            <div className="ud-info-row">
              <span className="ud-info-label">Package</span>
              <span className="ud-info-value">{profile?.package_type || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* INFO CARDS */}
        <div className="ud-info-cards">
          <div className="ud-info-card">
            <div className="ud-card-icon">🔐</div>
            <h4>Secure Access</h4>
            <p>Your license is protected and secure</p>
          </div>
          <div className="ud-info-card">
            <div className="ud-card-icon">🛡️</div>
            <h4>Protected</h4>
            <p>Machine-bound license protection</p>
          </div>
          <div className="ud-info-card">
            <div className="ud-card-icon">📞</div>
            <h4>Support</h4>
            <p>Contact admin for assistance</p>
          </div>
        </div>

        {profile?.active && !licenseCountdown.expired && (
          <div className="ud-success-banner">✅ Your license is active and valid!</div>
        )}
      </main>

      <ChangePassword
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSuccess={msg => setSuccess(msg)}
      />

      <Footer variant="user" />
    </div>
  );
}
